import {
  ButtonsRowWrapper,
  PreviewTableHeaderWrapper,
  TableExpandedTitle,
} from '../DataModelerCommonUI';
import { Loader } from '../../../../components/Loaders';
import { SecondaryButton } from '../../../../components/Button';
import {
  LoaderWrapper,
  PreviewTableFormWrapper,
  PreviewTableRowsWrapper,
} from './DataModelerPreviewDataGridUI';
import { FieldError } from '../../../../components/Form';
import { ShowSqlModalDisplay } from '../../../../components/Chart/ShowSQLModal';
import React, { useState } from 'react';
import { IPreviewDataRows } from '../utils';
import {
  ROW_HEIGHT,
  useCdwTableGridColumnWidths,
  usePreviewDataGridCellRenderer,
} from './renderUtils';
import { MultiGridTable } from '../../../../components/MultiGridTable';

type PreviewDataGridHeight = 'normal' | 'small';

interface IProps {
  loading: boolean;
  previewCachedData: IPreviewDataRows | null;
  loadPreviewData: () => void;
  resetPreviewData: () => void;
  headerName: string;
  height?: PreviewDataGridHeight;
  lastColumnIsResult: boolean;
}

const DataModelerPreviewDataGrid = ({
  loading,
  previewCachedData,
  loadPreviewData,
  resetPreviewData,
  headerName,
  lastColumnIsResult,
  height = 'normal',
}: IProps) => {
  const [showSqlOpen, setShowSqlOpen] = useState(false);

  const { cellRenderer, rowCount, exportToCsv } =
    usePreviewDataGridCellRenderer({
      previewCachedData,
      lastColumnIsResult,
    });

  const { getColumnWidth, onResize, multiGridRef } =
    useCdwTableGridColumnWidths({
      previewData: previewCachedData,
      hasVerticalScrollbar: height === 'normal',
    });

  if (loading) {
    return (
      <LoaderWrapper $small={height === 'small'}>
        <Loader />
      </LoaderWrapper>
    );
  }
  // if (!previewCachedData) {
  //   return null;
  // }
  return (
    <>
      <PreviewTableHeaderWrapper>
        <TableExpandedTitle>Preview {headerName} Values</TableExpandedTitle>
        <ButtonsRowWrapper>
          <SecondaryButton
            onClick={() => setShowSqlOpen(true)}
            isOutlined={true}
            disabled={!previewCachedData?.sqlQuery}
            trackContext="data modeler"
            trackAction="preview - show sql"
          >
            Show SQL
          </SecondaryButton>
          <SecondaryButton
            onClick={exportToCsv}
            isOutlined={true}
            disabled={
              !previewCachedData || previewCachedData.header.length === 0
            }
            trackContext="data modeler"
            trackAction="preview - export csv"
          >
            Export To CSV
          </SecondaryButton>
          <SecondaryButton
            isOutlined={true}
            onClick={loadPreviewData}
            trackContext="data modeler"
            trackAction="preview - refresh"
          >
            Refresh
          </SecondaryButton>
          <SecondaryButton
            isOutlined={true}
            onClick={resetPreviewData}
            trackContext="data modeler"
            trackAction="preview - close"
          >
            Close Preview
          </SecondaryButton>
        </ButtonsRowWrapper>
      </PreviewTableHeaderWrapper>
      <PreviewTableFormWrapper>
        {previewCachedData && previewCachedData.errors.length > 0 && (
          <div>
            <FieldError
              type={'custom'}
              customError={previewCachedData?.errors.join('; ') ?? ''}
            />
          </div>
        )}
        {previewCachedData && previewCachedData.header.length > 0 && (
          <PreviewTableRowsWrapper $small={height === 'small'}>
            <MultiGridTable
              ref={multiGridRef}
              columnCount={previewCachedData.header.length}
              rowCount={rowCount}
              cellRenderer={cellRenderer}
              rowHeight={ROW_HEIGHT}
              columnWidthRenderer={getColumnWidth}
              fixedRowCount={1}
              onResize={onResize}
            />
          </PreviewTableRowsWrapper>
        )}
        {previewCachedData?.sqlQuery && showSqlOpen && (
          <ShowSqlModalDisplay
            headerDisplay={'Preview Values SQL'}
            referenceSql={previewCachedData.sqlQuery}
            sqlType={'Preview Values'}
            close={() => setShowSqlOpen(false)}
          />
        )}
      </PreviewTableFormWrapper>
    </>
  );
};

export default DataModelerPreviewDataGrid;
